import React from "react";
import {
  AdvancedList,
  Badge,
  EmptyState,
  type AdvancedListSortDef,
} from "@west-tennessee-consulting/ui";
import { Users as UsersIcon, ExternalLink } from "lucide-react";
import type { AccessibleContact } from "../use-user-access-grants";

const SORTS: Array<AdvancedListSortDef<AccessibleContact>> = [
  {
    id: "contact",
    label: "Contact",
    compare: (a, b) =>
      (a.contactName || a.contactEmail || "").localeCompare(
        b.contactName || b.contactEmail || ""
      ),
  },
  {
    id: "company",
    label: "Company",
    compare: (a, b) => (a.companyName || "").localeCompare(b.companyName || ""),
  },
];

/**
 * The per-contact grant list shown on a user's Access tab (both the
 * UserDetailModal and the legacy admin-registry detail tab consume this same
 * component — was previously duplicated byte-for-byte in both places).
 * Search + sort via AdvancedList: the backing query (useUserAccessGrants) has
 * no row limit, so a heavily-delegated employee's grant list can genuinely
 * exceed 20 with no way to find one contact beyond scrolling.
 */
export function AccessGrantList({
  title,
  emptyTitle,
  emptyDescription,
  grants,
  showAccessLevel = false,
  onOpenContact,
}: {
  title: string;
  emptyTitle: string;
  emptyDescription: string;
  grants: AccessibleContact[];
  showAccessLevel?: boolean;
  onOpenContact: (contactId: string) => void;
}) {
  if (grants.length === 0) {
    return (
      <EmptyState
        icon={UsersIcon}
        title={emptyTitle}
        description={emptyDescription}
      />
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <span className="text-xs font-medium text-muted-foreground">
        {title} ({grants.length})
      </span>
      <AdvancedList<AccessibleContact>
        items={grants}
        getId={(grant) => grant.grantId}
        searchFields={(grant) => [grant.contactName, grant.contactEmail, grant.companyName]}
        searchPlaceholder="Search contacts…"
        sorts={SORTS}
        maxHeight="20rem"
        renderItem={(grant) => (
          <button
            type="button"
            onClick={() => onOpenContact(grant.contactId)}
            className="w-full flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-left hover:bg-accent transition-colors"
          >
            <span className="flex flex-col min-w-0">
              <span className="text-sm font-medium truncate inline-flex items-center gap-1">
                {grant.contactName || grant.contactEmail || "Unnamed contact"}
                <ExternalLink className="h-3 w-3 text-muted-foreground shrink-0" />
              </span>
              {(grant.companyName || grant.contactEmail) && (
                <span className="text-xs text-muted-foreground truncate">
                  {grant.companyName || grant.contactEmail}
                </span>
              )}
            </span>
            {showAccessLevel && grant.accessLevel && (
              <Badge variant="secondary" className="shrink-0 capitalize">
                {grant.accessLevel.replace(/_/g, " ")}
              </Badge>
            )}
          </button>
        )}
      />
    </div>
  );
}
